// @ts-nocheck
/**
 * ツインキーシステム スタイル定義
 * @module twinKey/ui/styles/twinKeyStyles
 */

// ============================================================
// カラーパレット
// ============================================================

export const colors = {
  // 背景
  background: '#0a0a1a',
  backgroundLight: '#1a1a2e',
  backgroundCard: '#16213e',
  backgroundHover: '#1f3460',

  // テキスト
  textPrimary: '#ffffff',
  textSecondary: '#a0a0b0',
  textMuted: '#606070',

  // アクセント
  primary: '#4a90d9',
  primaryHover: '#5aa0e9',
  secondary: '#9d4ce6',
  success: '#4caf50',
  warning: '#ff9800',
  danger: '#f44336',
  info: '#2196f3',

  // 属性
  attribute: {
    '炎': '#ff6b6b',
    '水': '#6b9eff',
    '光': '#ffd700',
    '闇': '#9d4ce6',
    '原始': '#6bff6b',
    '未来': '#00ffff',
    'なし': '#888888',
  },

  // レアリティ
  rarity: {
    C: '#888888',
    UC: '#4a4a4a',
    R: '#3498db',
    SR: '#9b59b6',
    UR: '#f39c12',
    HR: '#e74c3c',
    SEC: '#e91e63',
    ALT: '#00bcd4',
    SP: '#ff5722',
    GR: '#ffd700',
  },
};

// ============================================================
// 共通スタイル
// ============================================================

export const baseStyles = {
  // コンテナ
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: colors.background,
    color: colors.textPrimary,
    fontFamily: "'Noto Sans JP', sans-serif",
  },

  // ヘッダー
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 20px',
    backgroundColor: colors.backgroundLight,
    borderBottom: `1px solid ${colors.backgroundCard}`,
  },

  // メインコンテンツ
  main: {
    flex: 1,
    padding: '20px',
    overflowY: 'auto',
  },

  // カード
  card: {
    backgroundColor: colors.backgroundCard,
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px',
  },

  // ボタン
  button: {
    padding: '12px 24px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    transition: 'all 0.2s ease',
  },

  buttonPrimary: {
    backgroundColor: colors.primary,
    color: colors.textPrimary,
  },

  buttonSecondary: {
    backgroundColor: colors.backgroundLight,
    color: colors.textPrimary,
    border: `1px solid ${colors.primary}`,
  },

  buttonDanger: {
    backgroundColor: colors.danger,
    color: colors.textPrimary,
  },

  buttonDisabled: {
    backgroundColor: colors.backgroundLight,
    color: colors.textMuted,
    cursor: 'not-allowed',
    opacity: 0.5,
  },

  // グリッド
  grid: {
    display: 'grid',
    gap: '16px',
  },

  // フレックス
  flexRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },

  flexColumn: {
    display: 'flex',
    flexDirection: 'column',
  },

  flexCenter: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  flexBetween: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
};

// ============================================================
// ハブ画面スタイル
// ============================================================

export const hubStyles = {
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: '30px',
    background: 'linear-gradient(135deg, #ffd700, #ff6b6b)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },

  menuGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
    maxWidth: '500px',
    margin: '0 auto',
  },

  menuButton: {
    ...baseStyles.button,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '24px',
    backgroundColor: colors.backgroundCard,
    borderRadius: '12px',
    minHeight: '120px',
    justifyContent: 'center',
  },

  menuIcon: {
    fontSize: '32px',
    marginBottom: '8px',
  },

  menuLabel: {
    fontSize: '14px',
    color: colors.textPrimary,
  },

  statsCard: {
    ...baseStyles.card,
    marginTop: '30px',
  },
};

// ============================================================
// 鍵選択画面スタイル
// ============================================================

export const keySelectionStyles = {
  keySlots: {
    display: 'flex',
    justifyContent: 'center',
    gap: '40px',
    marginBottom: '30px',
  },

  keySlot: {
    width: '150px',
    height: '200px',
    borderRadius: '12px',
    border: `2px dashed ${colors.textMuted}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  keySlotFilled: {
    border: `2px solid ${colors.primary}`,
    backgroundColor: colors.backgroundCard,
  },

  keyLabel: {
    fontSize: '14px',
    color: colors.textMuted,
    marginBottom: '8px',
  },

  preview: {
    ...baseStyles.card,
    marginBottom: '20px',
    padding: '20px',
  },

  previewTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '12px',
    color: colors.primary,
  },

  collectionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
    gap: '12px',
  },

  filterBar: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },

  filterSelect: {
    padding: '8px 12px',
    borderRadius: '6px',
    backgroundColor: colors.backgroundLight,
    color: colors.textPrimary,
    border: `1px solid ${colors.backgroundCard}`,
  },
};

// ============================================================
// パーティ画面スタイル
// ============================================================

export const partyStyles = {
  partySlots: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    marginBottom: '30px',
    flexWrap: 'wrap',
  },

  partySlot: {
    width: '120px',
    height: '160px',
    borderRadius: '8px',
    border: `2px dashed ${colors.textMuted}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
  },

  partySlotFilled: {
    border: `2px solid ${colors.success}`,
    backgroundColor: colors.backgroundCard,
  },

  slotNumber: {
    position: 'absolute',
    top: '4px',
    left: '8px',
    fontSize: '12px',
    color: colors.textMuted,
  },

  removeButton: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: colors.danger,
    color: colors.textPrimary,
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  monsterList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '12px',
  },
};

// ============================================================
// バトル画面スタイル
// ============================================================

export const battleStyles = {
  battleField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px',
    padding: '20px',
  },

  enemyArea: {
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
    minHeight: '150px',
  },

  allyArea: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    minHeight: '150px',
  },

  turnOrderBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    backgroundColor: colors.backgroundLight,
    borderRadius: '8px',
    overflowX: 'auto',
  },

  turnIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    border: '2px solid transparent',
    flexShrink: 0,
  },

  turnIconCurrent: {
    border: `2px solid ${colors.warning}`,
    boxShadow: `0 0 10px ${colors.warning}`,
  },

  actionPanel: {
    display: 'flex',
    gap: '12px',
    padding: '16px',
    backgroundColor: colors.backgroundCard,
    borderRadius: '8px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },

  skillButton: {
    ...baseStyles.button,
    minWidth: '100px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },

  skillCooldown: {
    fontSize: '11px',
    color: colors.textMuted,
  },

  controlBar: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '16px',
  },

  battleLog: {
    maxHeight: '150px',
    overflowY: 'auto',
    padding: '12px',
    backgroundColor: colors.backgroundLight,
    borderRadius: '8px',
    fontSize: '13px',
  },

  logEntry: {
    marginBottom: '4px',
    padding: '4px 0',
    borderBottom: `1px solid ${colors.backgroundCard}`,
  },
};

// ============================================================
// モンスターカードスタイル
// ============================================================

export const monsterCardStyles = {
  card: {
    width: '100px',
    backgroundColor: colors.backgroundCard,
    borderRadius: '8px',
    padding: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
  },

  cardSelected: {
    border: `2px solid ${colors.primary}`,
    boxShadow: `0 0 10px ${colors.primary}`,
  },

  cardBattle: {
    width: '100px',
    minHeight: '120px',
  },

  name: {
    fontSize: '11px',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: '4px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  level: {
    fontSize: '10px',
    color: colors.textSecondary,
    textAlign: 'center',
  },

  hpBar: {
    height: '6px',
    backgroundColor: colors.backgroundLight,
    borderRadius: '3px',
    marginTop: '6px',
    overflow: 'hidden',
  },

  hpFill: {
    height: '100%',
    backgroundColor: colors.success,
    transition: 'width 0.3s ease',
  },

  hpText: {
    fontSize: '10px',
    textAlign: 'center',
    marginTop: '2px',
    color: colors.textSecondary,
  },

  attributeBadge: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    fontSize: '12px',
  },

  rarityBadge: {
    position: 'absolute',
    top: '4px',
    left: '4px',
    fontSize: '9px',
    fontWeight: 'bold',
    padding: '1px 4px',
    borderRadius: '3px',
  },
};

// ============================================================
// ダンジョン画面スタイル
// ============================================================

export const dungeonStyles = {
  floorInfo: {
    textAlign: 'center',
    marginBottom: '20px',
  },

  floorNumber: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: colors.primary,
  },

  dungeonName: {
    fontSize: '14px',
    color: colors.textSecondary,
  },

  choiceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    maxWidth: '700px',
    margin: '0 auto',
  },

  choiceCard: {
    ...baseStyles.card,
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'all 0.2s ease',
    border: '2px solid transparent',
  },

  choiceCardHover: {
    border: `2px solid ${colors.primary}`,
    transform: 'translateY(-4px)',
  },

  choiceIcon: {
    fontSize: '32px',
    marginBottom: '8px',
  },

  choiceName: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '4px',
  },

  choiceDescription: {
    fontSize: '12px',
    color: colors.textSecondary,
  },

  partyStatus: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    marginTop: '30px',
    flexWrap: 'wrap',
  },
};

// ============================================================
// 結果画面スタイル
// ============================================================

export const resultStyles = {
  resultTitle: {
    fontSize: '32px',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: '30px',
  },

  victory: {
    color: colors.success,
  },

  defeat: {
    color: colors.danger,
  },

  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
    marginBottom: '30px',
  },

  statItem: {
    ...baseStyles.card,
    textAlign: 'center',
  },

  statValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: colors.primary,
  },

  statLabel: {
    fontSize: '12px',
    color: colors.textSecondary,
  },

  rewardList: {
    ...baseStyles.card,
  },

  rewardItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: `1px solid ${colors.backgroundLight}`,
  },
};

// ============================================================
// ユーティリティ関数
// ============================================================

/**
 * 属性に応じた色を取得
 * @param {string} attribute - 属性
 * @returns {string} 色コード
 */
export function getAttributeColor(attribute) {
  return colors.attribute[attribute] || colors.attribute['なし'];
}

/**
 * レアリティに応じた色を取得
 * @param {string} rarity - レアリティ
 * @returns {string} 色コード
 */
export function getRarityColor(rarity) {
  return colors.rarity[rarity] || colors.rarity.C;
}

/**
 * HP割合に応じた色を取得
 * @param {number} percent - HP割合（0-100）
 * @returns {string} 色コード
 */
export function getHpColor(percent) {
  if (percent > 50) return colors.success;
  if (percent > 25) return colors.warning;
  return colors.danger;
}

/**
 * 属性アイコンを取得
 * @param {string} attribute - 属性
 * @returns {string} アイコン
 */
export function getAttributeIcon(attribute) {
  const icons = {
    '炎': '🔥',
    '水': '💧',
    '光': '✨',
    '闇': '🌙',
    '原始': '🌿',
    '未来': '⚡',
    'なし': '⚪',
  };
  return icons[attribute] || '⚪';
}

// ============================================================
// 統合スタイルオブジェクト（画面コンポーネント用）
// ============================================================

const styles = {
  // ユーティリティ関数
  getAttributeColor,
  getRarityColor,
  getHpColor,
  getAttributeIcon,

  // アプリコンテナ
  appContainer: {
    width: '100%',
    minHeight: '100vh',
    backgroundColor: colors.background,
    overflowX: 'hidden', // 横スクロール防止
    boxSizing: 'border-box',
  },

  // 共通
  container: {
    ...baseStyles.container,
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    width: '100%',
    maxWidth: '100vw', // ビューポート幅を超えない
    overflowX: 'hidden', // 横スクロール防止
    boxSizing: 'border-box',
  },

  header: {
    ...baseStyles.header,
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },

  backButton: {
    ...baseStyles.button,
    backgroundColor: 'transparent',
    color: colors.textSecondary,
    padding: '8px 16px',
    fontSize: '14px',
  },

  title: {
    ...hubStyles.title,
    margin: 0,
  },

  subtitle: {
    fontSize: '14px',
    color: colors.textSecondary,
  },

  sectionTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '16px',
    color: colors.textPrimary,
  },

  primaryButton: {
    ...baseStyles.button,
    ...baseStyles.buttonPrimary,
    padding: '14px 32px',
    fontSize: '16px',
  },

  secondaryButton: {
    ...baseStyles.button,
    ...baseStyles.buttonSecondary,
    padding: '14px 32px',
    fontSize: '16px',
  },

  disabledButton: {
    ...baseStyles.buttonDisabled,
  },

  emptyMessage: {
    textAlign: 'center',
    padding: '40px',
    color: colors.textMuted,
    fontSize: '16px',
  },

  warningText: {
    color: colors.warning,
    fontSize: '14px',
    textAlign: 'center',
    marginTop: '8px',
  },

  // ============================================================
  // ハブ画面
  // ============================================================

  // ハブ画面コンテナ（ヘッダー・フッター固定レイアウト）
  hubContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: colors.background,
    color: colors.textPrimary,
    overflow: 'hidden',
  },

  // ハブ画面ヘッダー（固定）
  hubHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 20px',
    backgroundColor: colors.backgroundLight,
    borderBottom: `1px solid ${colors.backgroundCard}`,
    flexShrink: 0,
  },

  hubHeaderCenter: {
    textAlign: 'center',
  },

  hubHeaderSpacer: {
    width: '60px', // 戻るボタンと同じ幅で右側のバランスを取る
  },

  hubTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    margin: 0,
    background: 'linear-gradient(135deg, #ffd700, #ff6b6b)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },

  // ハブ画面スクロールコンテンツ
  hubScrollContent: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '20px',
  },

  // スクロールコンテンツ内のラッパー（中央寄せ用）
  hubScrollContentInner: {
    maxWidth: '600px',
    margin: '0 auto',
  },

  // ハブ画面フッター（固定）
  hubFooter: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '16px 20px',
    backgroundColor: colors.backgroundLight,
    borderTop: `1px solid ${colors.backgroundCard}`,
    flexShrink: 0,
  },

  // 旧hubContent（互換性のため残す）
  hubContent: {
    padding: '20px',
    maxWidth: '600px',
    margin: '0 auto',
  },

  hubSection: {
    ...baseStyles.card,
    marginBottom: '20px',
  },

  keySlots: {
    ...keySelectionStyles.keySlots,
    marginBottom: '16px',
    flexWrap: 'wrap', // モバイル対応
    gap: '16px', // モバイル用に小さく
  },

  keySlot: {
    ...keySelectionStyles.keySlot,
    backgroundColor: colors.backgroundCard,
    width: '120px', // モバイル用に小さく
    height: '160px', // モバイル用に小さく
    flexShrink: 0,
  },

  emptyKeySlot: {
    backgroundColor: 'transparent',
  },

  keyPlusIcon: {
    fontSize: '32px',
    color: colors.textMuted,
    alignSelf: 'center',
  },

  keyPlusIconSmall: {
    fontSize: '20px',
    color: colors.textMuted,
  },

  emptySlotContent: {
    textAlign: 'center',
  },

  emptySlotIcon: {
    fontSize: '32px',
    marginBottom: '8px',
    opacity: 0.5,
  },

  emptySlotText: {
    fontSize: '14px',
    color: colors.textMuted,
  },

  keyCardPreview: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '8px',
  },

  keyCardAttribute: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  },

  keyCardInfo: {
    textAlign: 'center',
  },

  keyCardName: {
    fontSize: '14px',
    fontWeight: 'bold',
  },

  keyCardRarity: {
    fontSize: '12px',
    color: colors.textSecondary,
  },

  dungeonPreview: {
    padding: '12px',
    backgroundColor: colors.backgroundLight,
    borderRadius: '8px',
  },

  dungeonPreviewContent: {
    textAlign: 'center',
  },

  dungeonPreviewTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: '8px',
  },

  dungeonPreviewStats: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'center',
    fontSize: '13px',
    color: colors.textSecondary,
  },

  emptyParty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '24px',
    cursor: 'pointer',
    borderRadius: '8px',
    border: `2px dashed ${colors.textMuted}`,
    transition: 'all 0.2s ease',
  },

  emptyPartyIcon: {
    fontSize: '40px',
    marginBottom: '8px',
    opacity: 0.5,
  },

  emptyPartyText: {
    fontSize: '14px',
    color: colors.textMuted,
  },

  partyPreview: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '8px',
    transition: 'background 0.2s ease',
  },

  partyMemberPreview: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '8px',
    backgroundColor: colors.backgroundLight,
    borderRadius: '6px',
    border: '2px solid transparent',
    minWidth: '80px',
  },

  memberIcon: {
    fontSize: '20px',
  },

  memberName: {
    fontSize: '11px',
    fontWeight: 'bold',
    marginTop: '4px',
    maxWidth: '70px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  memberLevel: {
    fontSize: '10px',
    color: colors.textSecondary,
  },

  editPartyHint: {
    fontSize: '12px',
    color: colors.textMuted,
    alignSelf: 'center',
    marginLeft: 'auto',
  },

  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
  },

  statItem: {
    textAlign: 'center',
    padding: '12px',
    backgroundColor: colors.backgroundLight,
    borderRadius: '6px',
  },

  statLabel: {
    fontSize: '12px',
    color: colors.textSecondary,
    marginBottom: '4px',
  },

  statValue: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: colors.textPrimary,
  },

  hubActions: {
    textAlign: 'center',
    marginTop: '20px',
  },

  // 施設ボタン
  facilityButtons: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },

  facilityButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '16px 24px',
    backgroundColor: colors.backgroundLight,
    border: `2px solid ${colors.secondary}`,
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minWidth: '120px',
    color: colors.textPrimary,
  },

  facilityIcon: {
    fontSize: '32px',
  },

  facilityLabel: {
    fontSize: '14px',
    fontWeight: 'bold',
  },

  // ============================================================
  // 鍵選択画面
  // ============================================================

  // 鍵選択画面コンテナ（ヘッダー・フッター固定レイアウト）
  keySelectionContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: colors.background,
    color: colors.textPrimary,
    overflow: 'hidden',
  },

  // 鍵選択画面ヘッダー（固定）- タイトル + 選択済み鍵 + フィルター
  keySelectionHeader: {
    flexShrink: 0,
    backgroundColor: colors.backgroundLight,
  },

  // タイトルバー
  keySelectionTitleBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 20px',
    borderBottom: `1px solid ${colors.backgroundCard}`,
  },

  keySelectionTitleCenter: {
    textAlign: 'center',
  },

  keySelectionTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    margin: 0,
    color: colors.textPrimary,
  },

  // 鍵選択画面スクロールコンテンツ
  keySelectionScrollContent: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '16px 20px',
  },

  // 鍵選択画面フッター（固定）
  keySelectionFooter: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '16px 20px',
    backgroundColor: colors.backgroundLight,
    borderTop: `1px solid ${colors.backgroundCard}`,
    flexShrink: 0,
  },

  selectedKeysBar: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    padding: '12px 20px',
    backgroundColor: colors.backgroundLight,
    borderBottom: `1px solid ${colors.backgroundCard}`,
  },

  selectedKeySlot: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: colors.backgroundCard,
    borderRadius: '6px',
    minWidth: '140px',
    cursor: 'pointer',
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: 'transparent',
  },

  activeKeySlot: {
    borderColor: colors.primary,
    boxShadow: `0 0 8px ${colors.primary}`,
  },

  selectedKeyIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
  },

  selectedKeyInfo: {
    flex: 1,
  },

  selectedKeyName: {
    fontSize: '12px',
    fontWeight: 'bold',
  },

  selectedKeyRarity: {
    fontSize: '10px',
    color: colors.textSecondary,
  },

  emptySelectedKey: {
    fontSize: '14px',
    color: colors.textMuted,
    textAlign: 'center',
    width: '100%',
  },

  clearButton: {
    backgroundColor: colors.danger,
    color: colors.textPrimary,
    border: 'none',
    borderRadius: '50%',
    width: '20px',
    height: '20px',
    cursor: 'pointer',
    fontSize: '12px',
  },

  filterBar: {
    ...keySelectionStyles.filterBar,
    padding: '12px 20px',
    backgroundColor: colors.backgroundCard,
  },

  searchInput: {
    flex: 1,
    padding: '10px 14px',
    borderRadius: '6px',
    backgroundColor: colors.backgroundLight,
    color: colors.textPrimary,
    border: `1px solid ${colors.backgroundCard}`,
    fontSize: '14px',
    minWidth: '120px',
  },

  filterSelect: {
    ...keySelectionStyles.filterSelect,
    minWidth: '90px',
  },

  cardGridContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 20px',
  },

  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '12px',
  },

  keyCardItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: colors.backgroundCard,
    borderRadius: '8px',
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: 'transparent',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
  },

  cardAttributeBadge: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    marginRight: '12px',
    flexShrink: 0,
  },

  cardItemInfo: {
    flex: 1,
  },

  cardItemName: {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '4px',
  },

  cardItemMeta: {
    display: 'flex',
    gap: '12px',
    fontSize: '12px',
    color: colors.textSecondary,
    marginBottom: '4px',
  },

  cardItemRarity: {},
  cardItemCost: {},

  cardItemStats: {
    fontSize: '12px',
    color: colors.textMuted,
  },

  selectedMark: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    backgroundColor: colors.primary,
    color: colors.textPrimary,
    borderRadius: '50%',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
  },

  confirmBar: {
    position: 'sticky',
    bottom: 0,
    padding: '16px 20px',
    backgroundColor: colors.backgroundLight,
    borderTop: `1px solid ${colors.backgroundCard}`,
    textAlign: 'center',
  },

  // ============================================================
  // パーティ画面
  // ============================================================

  partyScreenContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
  },

  partySection: {
    padding: '16px 20px',
    borderBottom: `1px solid ${colors.backgroundCard}`,
  },

  partySlots: {
    ...partyStyles.partySlots,
    gap: '8px',
  },

  partySlot: {
    ...partyStyles.partySlot,
    width: '140px',
    height: '100px',
    padding: '8px',
    border: '2px solid transparent',
    backgroundColor: colors.backgroundCard,
  },

  emptyPartySlot: {
    ...partyStyles.partySlot,
    width: '140px',
    height: '100px',
    backgroundColor: 'transparent',
  },

  emptySlotNumber: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: colors.textMuted,
    marginBottom: '8px',
  },

  reorderButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },

  reorderButton: {
    backgroundColor: colors.backgroundLight,
    border: 'none',
    borderRadius: '4px',
    padding: '4px 8px',
    cursor: 'pointer',
    color: colors.textSecondary,
    fontSize: '10px',
  },

  partyMemberContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
    cursor: 'pointer',
  },

  memberAttributeIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    flexShrink: 0,
  },

  memberDetails: {
    flex: 1,
  },

  memberNameText: {
    fontSize: '12px',
    fontWeight: 'bold',
  },

  memberLevelText: {
    fontSize: '10px',
    color: colors.textSecondary,
  },

  memberStatsText: {
    fontSize: '10px',
    color: colors.textMuted,
  },

  removeButton: {
    ...partyStyles.removeButton,
    position: 'relative',
    top: 0,
    right: 0,
  },

  partyTotalStats: {
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    marginTop: '12px',
    padding: '12px',
    backgroundColor: colors.backgroundLight,
    borderRadius: '6px',
  },

  totalStatItem: {
    textAlign: 'center',
  },

  totalStatLabel: {
    fontSize: '12px',
    color: colors.textSecondary,
  },

  totalStatValue: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: colors.primary,
  },

  monsterSelectionSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: '16px 20px 0',
  },

  filterRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px',
  },

  searchInputSmall: {
    flex: 1,
    padding: '8px 12px',
    borderRadius: '6px',
    backgroundColor: colors.backgroundLight,
    color: colors.textPrimary,
    border: `1px solid ${colors.backgroundCard}`,
    fontSize: '13px',
  },

  filterSelectSmall: {
    padding: '8px 10px',
    borderRadius: '6px',
    backgroundColor: colors.backgroundLight,
    color: colors.textPrimary,
    border: `1px solid ${colors.backgroundCard}`,
    fontSize: '13px',
  },

  monsterListContainer: {
    flex: 1,
    overflowY: 'auto',
    paddingBottom: '16px',
  },

  monsterList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  monsterListItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 12px',
    backgroundColor: colors.backgroundCard,
    borderRadius: '8px',
    border: '2px solid transparent',
  },

  monsterItemMain: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    cursor: 'pointer',
  },

  monsterAttrIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    marginRight: '10px',
    flexShrink: 0,
  },

  monsterItemInfo: {
    flex: 1,
  },

  monsterItemName: {
    fontSize: '13px',
    fontWeight: 'bold',
  },

  monsterItemMeta: {
    display: 'flex',
    gap: '8px',
    fontSize: '11px',
    color: colors.textSecondary,
    marginTop: '2px',
  },

  monsterItemStats: {
    fontSize: '11px',
    color: colors.textMuted,
    marginTop: '2px',
  },

  addButton: {
    ...baseStyles.button,
    backgroundColor: colors.primary,
    color: colors.textPrimary,
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 'bold',
  },

  // モーダル
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },

  modalContent: {
    backgroundColor: colors.backgroundCard,
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '400px',
    width: '100%',
    maxHeight: '80vh',
    overflowY: 'auto',
    position: 'relative',
  },

  modalCloseButton: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    backgroundColor: 'transparent',
    border: 'none',
    color: colors.textSecondary,
    fontSize: '24px',
    cursor: 'pointer',
  },

  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },

  modalAttrIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  },

  modalMonsterName: {
    fontSize: '18px',
    fontWeight: 'bold',
  },

  modalMonsterMeta: {
    fontSize: '12px',
    color: colors.textSecondary,
    marginTop: '4px',
  },

  modalSectionTitle: {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '12px',
    color: colors.textSecondary,
  },

  modalStatsSection: {
    marginBottom: '20px',
  },

  modalStatsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
  },

  modalStatItem: {
    padding: '10px',
    backgroundColor: colors.backgroundLight,
    borderRadius: '6px',
    textAlign: 'center',
  },

  modalStatLabel: {
    fontSize: '11px',
    color: colors.textSecondary,
  },

  modalStatValue: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: colors.textPrimary,
    marginTop: '4px',
  },

  modalSkillsSection: {
    marginBottom: '20px',
  },

  modalSkillsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  modalSkillItem: {
    padding: '10px',
    backgroundColor: colors.backgroundLight,
    borderRadius: '6px',
  },

  skillHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '4px',
  },

  skillName: {
    fontSize: '13px',
    fontWeight: 'bold',
    color: colors.primary,
  },

  skillCooldown: {
    fontSize: '11px',
    color: colors.textMuted,
  },

  skillDescription: {
    fontSize: '12px',
    color: colors.textSecondary,
  },

  noSkills: {
    color: colors.textMuted,
    fontSize: '13px',
  },

  modalCategorySection: {
    fontSize: '12px',
    color: colors.textSecondary,
  },

  categoryLabel: {
    marginRight: '8px',
  },

  categoryValue: {
    color: colors.primary,
  },

  // ============================================================
  // ダンジョン画面
  // ============================================================

  dungeonHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    backgroundColor: colors.backgroundLight,
  },

  dungeonInfo: {},

  dungeonName: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: colors.textPrimary,
    margin: 0,
  },

  dungeonSubInfo: {
    fontSize: '12px',
    color: colors.textSecondary,
    marginTop: '4px',
  },

  floorInfo: {
    textAlign: 'right',
  },

  currentFloorLabel: {
    fontSize: '11px',
    color: colors.textSecondary,
  },

  currentFloorNumber: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: colors.primary,
  },

  totalFloors: {
    fontSize: '14px',
    color: colors.textMuted,
  },

  progressBarContainer: {
    padding: '0 20px',
    marginTop: '8px',
    position: 'relative',
  },

  progressBar: {
    height: '8px',
    backgroundColor: colors.primary,
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },

  progressText: {
    fontSize: '11px',
    color: colors.textSecondary,
    textAlign: 'center',
    marginTop: '4px',
  },

  partyStatusBar: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 20px',
    overflowX: 'auto',
  },

  miniMember: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px',
    backgroundColor: colors.backgroundCard,
    borderRadius: '6px',
    minWidth: '120px',
  },

  miniMemberIcon: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    flexShrink: 0,
  },

  miniMemberInfo: {
    flex: 1,
  },

  miniMemberNameRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '4px',
    width: '100%',
  },

  miniMemberName: {
    fontSize: '11px',
    fontWeight: 'bold',
    maxWidth: '50px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  miniMemberLevel: {
    fontSize: '9px',
    fontWeight: 'bold',
    color: '#ffd700',
    backgroundColor: 'rgba(255, 215, 0, 0.2)',
    padding: '1px 4px',
    borderRadius: '3px',
    flexShrink: 0,
  },

  miniHpBarContainer: {
    width: '100%',
    height: '4px',
    backgroundColor: colors.backgroundLight,
    borderRadius: '2px',
    marginTop: '2px',
    overflow: 'hidden',
  },

  miniHpBar: {
    height: '100%',
    transition: 'width 0.3s ease',
  },

  miniHpText: {
    fontSize: '9px',
    color: colors.textMuted,
    marginTop: '1px',
  },

  floorChoicesSection: {
    padding: '20px',
    flex: 1,
  },

  choiceTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: '20px',
    color: colors.textPrimary,
  },

  floorChoicesGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxWidth: '500px',
    margin: '0 auto',
  },

  floorChoiceCard: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    backgroundColor: colors.backgroundCard,
    borderRadius: '10px',
    border: '2px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  floorChoiceIcon: {
    fontSize: '32px',
    marginRight: '16px',
  },

  floorChoiceInfo: {
    flex: 1,
  },

  floorChoiceType: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '4px',
  },

  floorChoiceDescription: {
    fontSize: '12px',
    color: colors.textSecondary,
  },

  floorEnemyPreview: {
    fontSize: '11px',
    color: colors.danger,
    marginTop: '4px',
  },

  floorRewardPreview: {
    fontSize: '11px',
    color: colors.warning,
    marginTop: '2px',
  },

  floorChoiceArrow: {
    fontSize: '20px',
    color: colors.textMuted,
  },

  dungeonEffects: {
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
    padding: '12px',
    fontSize: '13px',
    color: colors.textSecondary,
  },

  dungeonGimmick: {},
  dungeonTime: {},

  retreatSection: {
    padding: '20px',
    textAlign: 'center',
    borderTop: `1px solid ${colors.backgroundCard}`,
  },

  retreatButton: {
    ...baseStyles.button,
    backgroundColor: colors.backgroundLight,
    color: colors.danger,
    border: `1px solid ${colors.danger}`,
    padding: '12px 24px',
  },

  retreatWarning: {
    fontSize: '11px',
    color: colors.textMuted,
    marginTop: '8px',
  },

  // ============================================================
  // バトル画面
  // ============================================================

  battleContainer: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: colors.background,
    color: colors.textPrimary,
  },

  battleHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: colors.backgroundLight,
  },

  battleInfo: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
  },

  turnCounter: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: colors.warning,
  },

  battleType: {
    fontSize: '14px',
    color: colors.textSecondary,
  },

  battleControls: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },

  controlButton: {
    ...baseStyles.button,
    padding: '6px 12px',
    fontSize: '12px',
    backgroundColor: colors.backgroundCard,
  },

  activeControl: {
    backgroundColor: colors.primary,
  },

  speedControls: {
    display: 'flex',
    gap: '4px',
  },

  speedButton: {
    ...baseStyles.button,
    padding: '6px 10px',
    fontSize: '11px',
    backgroundColor: colors.backgroundCard,
    minWidth: '32px',
  },

  activeSpeed: {
    backgroundColor: colors.info,
  },

  turnOrderBar: {
    ...battleStyles.turnOrderBar,
    margin: '8px 16px',
  },

  turnOrderUnit: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '6px',
    backgroundColor: colors.backgroundCard,
    borderRadius: '6px',
    border: '2px solid transparent',
    minWidth: '50px',
  },

  currentTurnUnit: {
    borderColor: colors.warning,
    boxShadow: `0 0 8px ${colors.warning}`,
  },

  turnOrderIcon: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    marginBottom: '4px',
  },

  turnOrderName: {
    fontSize: '9px',
    textAlign: 'center',
    maxWidth: '50px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  enemyArea: {
    ...battleStyles.enemyArea,
    padding: '16px',
    minHeight: '160px',
  },

  enemyUnits: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },

  battleLogArea: {
    padding: '8px 16px',
  },

  battleLog: {
    ...battleStyles.battleLog,
    maxHeight: '100px',
  },

  logEntry: {
    ...battleStyles.logEntry,
    fontSize: '12px',
  },

  allyArea: {
    ...battleStyles.allyArea,
    padding: '16px',
    minHeight: '160px',
  },

  allyUnits: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },

  battleUnit: {
    width: '90px',
    padding: '8px',
    backgroundColor: colors.backgroundCard,
    borderRadius: '8px',
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: 'transparent',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
    transition: 'all 0.2s ease',
  },

  enemyUnit: {},
  allyUnit: {},

  currentUnit: {
    boxShadow: `0 0 10px ${colors.warning}`,
  },

  validTargetUnit: {
    cursor: 'pointer',
    borderColor: colors.danger,
  },

  selectedUnit: {
    borderColor: colors.primary,
    boxShadow: `0 0 10px ${colors.primary}`,
  },

  unitAttrBadge: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    marginBottom: '4px',
  },

  unitName: {
    fontSize: '11px',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: '4px',
    maxWidth: '80px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  unitHpBarContainer: {
    width: '100%',
    height: '6px',
    backgroundColor: colors.backgroundLight,
    borderRadius: '3px',
    overflow: 'hidden',
  },

  unitHpBar: {
    height: '100%',
    transition: 'width 0.3s ease',
  },

  unitHpText: {
    fontSize: '9px',
    color: colors.textSecondary,
    marginTop: '2px',
    textAlign: 'center',
  },

  unitStatusIcons: {
    display: 'flex',
    gap: '2px',
    marginTop: '4px',
    fontSize: '12px',
  },

  statusIcon: {},
  currentIcon: {
    color: colors.warning,
  },

  deadOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: '8px',
  },

  actionPanel: {
    ...battleStyles.actionPanel,
    margin: '8px 16px',
    padding: '16px',
  },

  actionPanelHeader: {
    width: '100%',
    textAlign: 'center',
    marginBottom: '12px',
    fontSize: '14px',
    color: colors.textSecondary,
  },

  actionUnitName: {
    fontWeight: 'bold',
    color: colors.textPrimary,
  },

  actionButtons: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },

  actionButton: {
    ...battleStyles.skillButton,
    backgroundColor: colors.backgroundLight,
    minWidth: '80px',
  },

  selectedAction: {
    backgroundColor: colors.primary,
    boxShadow: `0 0 8px ${colors.primary}`,
  },

  disabledAction: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },

  escapeButton: {
    borderColor: colors.danger,
    color: colors.danger,
  },

  targetHint: {
    width: '100%',
    textAlign: 'center',
    marginTop: '12px',
    fontSize: '13px',
    color: colors.warning,
  },

  manualNextButton: {
    padding: '16px',
    textAlign: 'center',
  },

  battleResultOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },

  battleResultContent: {
    backgroundColor: colors.backgroundCard,
    borderRadius: '16px',
    padding: '32px',
    textAlign: 'center',
    maxWidth: '400px',
    width: '90%',
  },

  resultTitle: {
    fontSize: '28px',
    fontWeight: 'bold',
    marginBottom: '24px',
  },

  resultStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
  },

  resultStatItem: {
    padding: '12px',
    backgroundColor: colors.backgroundLight,
    borderRadius: '8px',
  },

  resultStatLabel: {
    fontSize: '11px',
    color: colors.textSecondary,
  },

  resultStatValue: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: colors.textPrimary,
    marginTop: '4px',
  },

  // ============================================================
  // 結果画面
  // ============================================================

  resultScreenContainer: {
    minHeight: '100vh',
    backgroundColor: colors.background,
    color: colors.textPrimary,
    position: 'relative',
    overflow: 'hidden',
  },

  resultBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  },

  resultMainContent: {
    position: 'relative',
    zIndex: 1,
    padding: '40px 20px',
    maxWidth: '600px',
    margin: '0 auto',
  },

  resultTitleSection: {
    textAlign: 'center',
    marginBottom: '30px',
  },

  resultTitleIcon: {
    fontSize: '64px',
    marginBottom: '16px',
  },

  resultTitleText: {
    fontSize: '32px',
    fontWeight: 'bold',
    margin: 0,
  },

  resultDungeonInfo: {
    textAlign: 'center',
    marginBottom: '30px',
  },

  dungeonResultName: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: '8px',
  },

  dungeonResultDetails: {
    fontSize: '14px',
    color: colors.textSecondary,
  },

  resultStatsSection: {
    marginBottom: '30px',
  },

  resultSectionTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '16px',
    color: colors.textSecondary,
  },

  resultStatsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
  },

  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px',
    backgroundColor: colors.backgroundCard,
    borderRadius: '10px',
  },

  statCardIcon: {
    fontSize: '24px',
  },

  statCardInfo: {},

  statCardLabel: {
    fontSize: '11px',
    color: colors.textSecondary,
  },

  statCardValue: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: colors.textPrimary,
  },

  statCardSubLabel: {
    fontSize: '12px',
    color: colors.textMuted,
    marginLeft: '4px',
  },

  resultRewardsSection: {
    marginBottom: '30px',
  },

  rewardsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },

  rewardItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px',
    backgroundColor: colors.backgroundCard,
    borderRadius: '10px',
  },

  rewardIcon: {
    fontSize: '28px',
  },

  rewardInfo: {},

  rewardLabel: {
    fontSize: '12px',
    color: colors.textSecondary,
  },

  rewardValue: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: colors.warning,
  },

  rewardItemsSection: {
    padding: '14px',
    backgroundColor: colors.backgroundCard,
    borderRadius: '10px',
  },

  rewardItemsTitle: {
    fontSize: '13px',
    fontWeight: 'bold',
    marginBottom: '10px',
    color: colors.textSecondary,
  },

  rewardItemsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },

  rewardItemEntry: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
  },

  itemIcon: {
    fontSize: '16px',
  },

  itemName: {
    flex: 1,
    color: colors.textPrimary,
  },

  itemCount: {
    color: colors.textSecondary,
  },

  // 素材セクション
  resultMaterialsSection: {
    marginBottom: '30px',
  },

  materialsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '10px',
  },

  materialItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    backgroundColor: colors.backgroundCard,
    borderRadius: '8px',
  },

  materialIcon: {
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    borderRadius: '8px',
    border: '2px solid',
    backgroundColor: colors.backgroundLight,
  },

  materialInfo: {
    flex: 1,
    minWidth: 0,
  },

  materialName: {
    fontSize: '12px',
    fontWeight: 'bold',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  materialCount: {
    fontSize: '14px',
    color: colors.textSecondary,
    fontWeight: 'bold',
  },

  resultPartySection: {
    marginBottom: '30px',
  },

  partyResultGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '12px',
  },

  partyMemberResult: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px',
    backgroundColor: colors.backgroundCard,
    borderRadius: '10px',
    border: '2px solid transparent',
    position: 'relative',
  },

  memberResultIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    flexShrink: 0,
  },

  memberResultInfo: {
    flex: 1,
  },

  memberResultName: {
    fontSize: '13px',
    fontWeight: 'bold',
  },

  memberResultLevel: {
    fontSize: '11px',
    color: colors.textSecondary,
  },

  memberResultHpBar: {
    width: '100%',
    height: '6px',
    backgroundColor: colors.backgroundLight,
    borderRadius: '3px',
    marginTop: '4px',
    overflow: 'hidden',
  },

  memberResultHpFill: {
    height: '100%',
    transition: 'width 0.3s ease',
  },

  memberResultHpText: {
    fontSize: '10px',
    color: colors.textMuted,
    marginTop: '2px',
  },

  memberDeadBadge: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    fontSize: '20px',
  },

  levelUpBadge: {
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    backgroundColor: colors.warning,
    color: colors.background,
    fontSize: '10px',
    fontWeight: 'bold',
    padding: '2px 6px',
    borderRadius: '4px',
  },

  resultActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    alignItems: 'center',
  },

  // ============================================================
  // フロアイベント結果モーダル
  // ============================================================

  eventModalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },

  eventModalContent: {
    backgroundColor: colors.backgroundCard,
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '400px',
    width: '100%',
    textAlign: 'center',
    border: `2px solid ${colors.primary}`,
    boxShadow: `0 0 30px rgba(74, 144, 217, 0.3)`,
  },

  eventModalTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    marginTop: 0,
    marginBottom: '16px',
    color: colors.textPrimary,
  },

  eventModalMessage: {
    fontSize: '16px',
    color: colors.textSecondary,
    marginBottom: '24px',
  },

  eventModalDetails: {
    textAlign: 'left',
    marginBottom: '24px',
  },

  healResultList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  healResultItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    backgroundColor: 'rgba(107, 255, 107, 0.1)',
    borderRadius: '6px',
  },

  healName: {
    fontSize: '14px',
    color: colors.textPrimary,
  },

  healAmount: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: colors.success,
  },

  treasureRewards: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  floorEventRewardItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    backgroundColor: 'rgba(255, 215, 0, 0.1)',
    borderRadius: '6px',
  },

  floorEventRewardValue: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#ffd700',
  },

  eventEffects: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  effectItem: {
    padding: '8px 12px',
    backgroundColor: 'rgba(107, 158, 255, 0.1)',
    borderRadius: '6px',
    fontSize: '14px',
    color: colors.textSecondary,
  },

  damageResultList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  damageResultItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    backgroundColor: 'rgba(255, 149, 0, 0.1)',
    borderRadius: '6px',
  },

  damageName: {
    fontSize: '14px',
    color: colors.textPrimary,
  },

  damageAmount: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: colors.danger,
  },

  eventModalButton: {
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: 'bold',
    backgroundColor: colors.primary,
    color: colors.textPrimary,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },

  // ============================================================
  // バトル演出スタイル
  // ============================================================

  // アクションバナー（画面中央）
  actionBannerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    zIndex: 100,
  },

  actionBanner: {
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    borderRadius: '12px',
    padding: '16px 32px',
    border: `2px solid ${colors.primary}`,
    boxShadow: `0 0 30px rgba(74, 144, 217, 0.5)`,
    animation: 'fadeInScale 0.3s ease-out',
    textAlign: 'center',
    minWidth: '200px',
  },

  actionBannerActor: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },

  actionBannerArrow: {
    fontSize: '24px',
    margin: '4px 0',
    color: colors.danger,
  },

  actionBannerTarget: {
    fontSize: '16px',
    color: colors.textSecondary,
    marginBottom: '12px',
  },

  actionBannerDamage: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: colors.danger,
  },

  actionBannerDamageCritical: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#ff4444',
    textShadow: '0 0 10px #ff4444',
  },

  actionBannerHeal: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: colors.success,
  },

  actionBannerSkill: {
    fontSize: '14px',
    color: colors.info,
    marginTop: '4px',
  },

  actionBannerDefeat: {
    fontSize: '16px',
    color: colors.secondary,
    marginTop: '8px',
    fontWeight: 'bold',
  },

  // ダメージフロート（ユニット上に表示）
  damageFloat: {
    position: 'absolute',
    top: '-20px',
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: '24px',
    fontWeight: 'bold',
    color: colors.danger,
    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
    animation: 'floatUp 1s ease-out forwards',
    pointerEvents: 'none',
    zIndex: 200,
  },

  damageFloatCritical: {
    fontSize: '32px',
    color: '#ff4444',
    textShadow: '0 0 10px #ff4444, 2px 2px 4px rgba(0, 0, 0, 0.8)',
  },

  healFloat: {
    position: 'absolute',
    top: '-20px',
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: '24px',
    fontWeight: 'bold',
    color: colors.success,
    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
    animation: 'floatUp 1s ease-out forwards',
    pointerEvents: 'none',
    zIndex: 200,
  },

  // ユニットハイライト状態
  unitHighlightAttacker: {
    boxShadow: `0 0 20px ${colors.success}, 0 0 40px ${colors.success}`,
    borderColor: colors.success,
    transform: 'scale(1.15)',
    zIndex: 50,
  },

  unitHighlightTarget: {
    boxShadow: `0 0 20px ${colors.danger}, 0 0 40px ${colors.danger}`,
    borderColor: colors.danger,
    animation: 'shake 0.3s ease-in-out',
    zIndex: 10,
  },

  unitHighlightDefend: {
    boxShadow: `0 0 20px ${colors.info}, 0 0 40px ${colors.info}`,
    borderColor: colors.info,
    transform: 'scale(1.1)',
    zIndex: 50,
  },

  // キーフレームアニメーション用のインラインスタイルシート
  // (React で inject する)
  battleAnimationKeyframes: `
    @keyframes floatUp {
      0% {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
      }
      100% {
        opacity: 0;
        transform: translateX(-50%) translateY(-40px);
      }
    }

    @keyframes shake {
      0%, 100% { transform: scale(1.15) translateX(0); }
      25% { transform: scale(1.15) translateX(-5px); }
      75% { transform: scale(1.15) translateX(5px); }
    }

    @keyframes fadeInScale {
      0% {
        opacity: 0;
        transform: scale(0.8);
      }
      100% {
        opacity: 1;
        transform: scale(1);
      }
    }

    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.7; }
    }
  `,
};

export default styles;
